import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../../config';
import { Theme as T } from '../../ui/theme';
import { GameState } from '../../systems/GameState';
import { OutfitRenderer } from '../../systems/OutfitRenderer';
import { OutfitIconRenderer } from '../../systems/OutfitIconRenderer';
import { OUTFITS_BY_ID, OUTFITS_BY_SLOT } from '../../data/outfits';
import { svgStringToTexture } from '../../utils/svgToTexture';
import { Button } from '../../ui/Button';
import { ToastManager } from '../../ui/ToastManager';
import { MiniGameRunner } from '../../systems/MiniGameRunner';
import { AudioManager } from '../../systems/AudioManager';
import { CharacterAnimator } from '../../systems/CharacterAnimator';
import type { Theme, OutfitSlot, HairStyle } from '../../types';

const THEMES: Theme[] = ['party', 'beach', 'school', 'sport', 'casual', 'formal'];
const THEME_LABELS: Record<Theme, { name: string; emoji: string }> = {
  party: { name: '파티 룩', emoji: '🎉' },
  beach: { name: '비치 룩', emoji: '🏖️' },
  school: { name: '학교 룩', emoji: '🎓' },
  sport: { name: '스포츠 룩', emoji: '⚽' },
  casual: { name: '캐주얼 룩', emoji: '😎' },
  formal: { name: '포멀 룩', emoji: '🎩' },
  holiday: { name: '홀리데이 룩', emoji: '🎄' },
  fantasy: { name: '판타지 룩', emoji: '🧚' },
};

const ROUND_DURATION_MS = 30_000;

export class DressUpChallengeScene extends Phaser.Scene {
  private theme!: Theme;
  private timeLeft = ROUND_DURATION_MS;
  private timerText!: Phaser.GameObjects.Text;
  private timerEvent!: Phaser.Time.TimerEvent;
  private progressBar!: Phaser.GameObjects.Rectangle;
  private characterSprite: Phaser.GameObjects.Image | null = null;
  private gridContainer: Phaser.GameObjects.Container | null = null;
  private tabContainer: Phaser.GameObjects.Container | null = null;
  private currentSlot: OutfitSlot = 'top';
  private toast!: ToastManager;
  private originalEquipped: Partial<Record<OutfitSlot, string>> = {};
  private trialEquipped: Partial<Record<OutfitSlot, string>> = {};

  constructor() { super('DressUpChallengeScene'); }

  async create(): Promise<void> {
    AudioManager.setScene(this);
    AudioManager.playBgm('minigame');
    this.cameras.main.setBackgroundColor('#fef7e6');
    this.toast = new ToastManager(this);
    this.theme = THEMES[Math.floor(Math.random() * THEMES.length)];
    this.timeLeft = ROUND_DURATION_MS;
    this.originalEquipped = { ...GameState.data.currentEquipped };
    this.trialEquipped = { ...GameState.data.currentEquipped };

    // 테마 헤더
    const t = THEME_LABELS[this.theme];
    this.add.text(GAME_WIDTH / 2, 68, `${t.emoji} ${t.name}으로 코디해요!`, {
      fontFamily: T.font.family,
      fontSize: '30px',
      fontStyle: '800',
      color: T.hex.text,
    }).setOrigin(0.5);

    // 타이머 chip
    const tChipG = this.add.graphics();
    tChipG.fillStyle(0xfff5f8, 1);
    tChipG.fillRoundedRect(GAME_WIDTH / 2 - 90, 104, 180, 40, 12);
    tChipG.lineStyle(1.5, T.colors.pink.light, 1);
    tChipG.strokeRoundedRect(GAME_WIDTH / 2 - 90, 104, 180, 40, 12);
    this.timerText = this.add.text(GAME_WIDTH / 2, 124, this.formatTime(), {
      fontFamily: T.font.family,
      fontSize: '20px',
      fontStyle: '800',
      color: T.hex.pinkDark,
    }).setOrigin(0.5);

    // Progress bar
    const barX = 200, barY = 18, barW = GAME_WIDTH - 400;
    this.add.rectangle(barX + barW / 2, barY, barW, 10, 0xdddddd).setOrigin(0.5);
    this.progressBar = this.add.rectangle(barX, barY, barW, 10, 0x44ee88).setOrigin(0, 0.5);

    this.timerEvent = this.time.addEvent({
      delay: 100,
      callback: () => this.tick(),
      loop: true,
    });

    // 캐릭터
    await this.refreshCharacter();

    // 카테고리 탭
    this.renderTabs();
    this.renderGrid();

    // 제출 버튼
    new Button(this, GAME_WIDTH - 140, GAME_HEIGHT - 50, '✨ 제출!', () => this.submit(), { width: 200, height: 56, style: 'gold' });

    // 포기 버튼
    new Button(this, 100, GAME_HEIGHT - 50, '← 포기', () => this.abort(), { width: 120, height: 48, style: 'secondary' });
  }

  private formatTime(): string {
    const s = Math.ceil(this.timeLeft / 1000);
    return `⏱ ${s}초`;
  }

  private tick(): void {
    this.timeLeft -= 100;
    this.timerText.setText(this.formatTime());
    const ratio = Math.max(0, this.timeLeft / ROUND_DURATION_MS);
    const barW = GAME_WIDTH - 400;
    this.progressBar.setDisplaySize(barW * ratio, 10);
    const r = Math.min(255, Math.floor((1 - ratio) * 510));
    const g = Math.min(255, Math.floor(ratio * 510));
    this.progressBar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 50));
    if (this.timeLeft <= 0) {
      this.submit();
    }
  }

  private renderTabs(): void {
    if (this.tabContainer) this.tabContainer.destroy();
    this.tabContainer = this.add.container(0, 0);
    const SLOTS: { id: OutfitSlot; label: string }[] = [
      { id: 'top', label: '상의' }, { id: 'bottom', label: '하의' }, { id: 'hair', label: '헤어' },
      { id: 'hairpin', label: '머리삔' }, { id: 'glasses', label: '안경' }, { id: 'bag', label: '가방' }, { id: 'shoes', label: '신발' },
    ];
    SLOTS.forEach((s, i) => {
      const x = GAME_WIDTH / 2 + 60 + (i % 4) * 130;
      const y = 170 + Math.floor(i / 4) * 60;
      const tabG = this.add.graphics();
      const active = s.id === this.currentSlot;
      tabG.fillStyle(active ? T.colors.pink.main : T.colors.pink.bg, 1);
      tabG.fillRoundedRect(x - 58, y - 22, 116, 44, 10);
      if (!active) {
        tabG.lineStyle(1.5, T.colors.pink.light, 1);
        tabG.strokeRoundedRect(x - 58, y - 22, 116, 44, 10);
      }
      const hitRect = this.add.rectangle(x, y, 116, 44, 0, 0);
      hitRect.setInteractive({ useHandCursor: true });
      hitRect.on('pointerdown', () => { this.currentSlot = s.id; this.renderTabs(); this.renderGrid(); });
      const label = this.add.text(x, y, s.label, {
        fontFamily: T.font.family,
        fontSize: '14px',
        fontStyle: '700',
        color: active ? '#ffffff' : T.hex.textMid,
      }).setOrigin(0.5);
      this.tabContainer!.add([tabG, hitRect, label]);
    });
  }

  private renderGrid(): void {
    if (this.gridContainer) this.gridContainer.destroy();
    this.gridContainer = this.add.container(0, 0);
    const items = OUTFITS_BY_SLOT[this.currentSlot] ?? [];
    const ownedIds = new Set(GameState.data.ownedOutfits.map(o => o.outfitId));
    const startX = GAME_WIDTH / 2 + 100;
    const startY = 320;

    items.forEach((it, i) => {
      const x = startX + (i % 3) * 130;
      const y = startY + Math.floor(i / 3) * 130;
      const owned = ownedIds.has(it.id);
      const equipped = this.trialEquipped[this.currentSlot] === it.id;
      const bg = this.add.rectangle(x, y, 110, 110, owned ? 0xffffff : 0xeeeeee);
      bg.setStrokeStyle(equipped ? 4 : 2, equipped ? 0xffc94c : 0xffd6e6);
      this.gridContainer!.add(bg);

      // Outfit icon
      const hairStyle = (it.slot === 'hair' && it.hairStyle) ? it.hairStyle as HairStyle : undefined;
      const iconSvg = OutfitIconRenderer.render(it.slot, it.color, hairStyle, it.iconStyle);
      const iconKey = `dressup_grid_${it.id}`;
      void svgStringToTexture(this, iconKey, iconSvg).then(() => {
        if (!this.scene.isActive() || !this.gridContainer) return;
        const icon = this.add.image(x, y - 8, iconKey).setScale(2.2);
        if (!owned) icon.setAlpha(0.4);
        this.gridContainer!.add(icon);
      });

      bg.setInteractive({ useHandCursor: owned });
      bg.on('pointerdown', () => {
        if (!owned) return;
        this.trialEquipped[this.currentSlot] = it.id;
        AudioManager.playSfx('equip');
        void this.refreshCharacter();
        this.renderGrid();
      });
    });
  }

  async refreshCharacter(): Promise<void> {
    const hairId = this.trialEquipped.hair;
    const fullKey = hairId ? `char_full_${hairId}` : null;
    const useKey = fullKey && this.textures.exists(fullKey) ? fullKey : 'character_bald';

    if (this.characterSprite) this.characterSprite.destroy();
    if (this.textures.exists(useKey)) {
      this.characterSprite = this.add.image(360, GAME_HEIGHT / 2 + 40, useKey);
      this.characterSprite.setScale(1.0);
    } else {
      // 최후 fallback (없을 일 거의 없음)
      const equipped = OutfitRenderer.resolveEquipped(this.trialEquipped, OUTFITS_BY_ID);
      const svg = OutfitRenderer.renderCharacterSVG(equipped);
      const key = 'dressup_character_main';
      await svgStringToTexture(this, key, svg);
      this.characterSprite = this.add.image(360, GAME_HEIGHT / 2 + 40, key);
      this.characterSprite.setScale(2.25);
    }
  }

  private submit(): void {
    this.timerEvent.remove();
    const score = this.calculateScore();
    const reward = MiniGameRunner.reportResult('dressUp', score);

    if (this.characterSprite) CharacterAnimator.cheer(this, this.characterSprite);
    this.toast.show(`🎉 ${score}점! ⭐ +${reward.stars}, 💰 +${reward.coins}`, 'gold');

    // 1.5초 후 허브로
    this.time.delayedCall(1800, () => {
      // 입고 있던 옷 원복
      GameState.data.currentEquipped = this.originalEquipped;
      GameState.persist();
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('MiniGameHubScene'));
    });
  }

  private abort(): void {
    this.timerEvent.remove();
    GameState.data.currentEquipped = this.originalEquipped;
    GameState.persist();
    this.cameras.main.fadeOut(200);
    this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('MiniGameHubScene'));
  }

  private calculateScore(): number {
    let total = 0;
    let count = 0;
    for (const slot of Object.keys(this.trialEquipped) as OutfitSlot[]) {
      const id = this.trialEquipped[slot];
      if (!id) continue;
      const o = OUTFITS_BY_ID[id];
      if (!o) continue;
      count++;
      if (o.themes.includes(this.theme)) total += 30;
      else total += 5;
    }
    // 0~210점 사이
    return Math.min(210, total);
  }
}
